import { useCallback } from 'react'
import { useWatch } from 'react-hook-form'
import { notification } from 'antd'

import { cashChangerLog } from '@/apis/aipass'
import { cashChangerStorageKey } from '@/constants/self-checkin-device-storage'
import { useDeviceSettingForm } from '@/hooks/self-checkin-device-settings/use-device-setting-form'
import { useCashChanger } from '@/hooks/use-cash-changer'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { toErrorMessage } from '@/libs/errors'

export type CashChangerSettingFormValues = {
  cashChangerSerialNumber: string
}

export const useCashChangerSetting = (hotelId: string) => {
  const { initialize, disconnect } = useCashChanger()
  const { errorHandler } = useErrorHandler()
  const { control, isDirty, isLoading, setIsLoading, handleSubmit, reset, cancel } = useDeviceSettingForm(getStoredCashChangerSetting)
  const cashChangerSerialNumber = useWatch({ control, name: 'cashChangerSerialNumber' })

  const connect = useCallback(
    async (serialNumber: string, onConnected?: () => void, onFailed?: () => void) => {
      let connector
      try {
        setIsLoading(true)
        connector = await initialize({ hotelId, serialNumber })
        notification.open({ message: '釣銭機の接続に成功しました' })
        cashChangerLog({ message: 'success' })
        onConnected?.()
      } catch (error) {
        errorHandler(error)
        cashChangerLog({ message: toErrorMessage(error) })
        onFailed?.()
      } finally {
        setIsLoading(false)
        disconnect(connector?.connection, connector?.device)
      }
    },
    [disconnect, errorHandler, hotelId, initialize, setIsLoading],
  )

  const saveValues = useCallback(
    async (values: CashChangerSettingFormValues) => {
      if (!values.cashChangerSerialNumber) {
        localStorage.setItem(cashChangerStorageKey, values.cashChangerSerialNumber)
        reset(values)
        return
      }

      await connect(
        values.cashChangerSerialNumber,
        () => {
          localStorage.setItem(cashChangerStorageKey, values.cashChangerSerialNumber)
          reset(values)
        },
        () => localStorage.setItem(cashChangerStorageKey, ''),
      )
    },
    [connect, reset],
  )

  // 接続テストは未編集時のみ実行できるため、フォームの値は保存済みの設定と一致する。
  // 保存済みの設定を壊さないよう localStorage は触らず、それ以外は保存時と同じ挙動にする
  const testConnection = useCallback(() => {
    connect(cashChangerSerialNumber).catch(errorHandler)
  }, [cashChangerSerialNumber, connect, errorHandler])

  return {
    control,
    isDirty,
    isLoading,
    canTestConnection: !!cashChangerSerialNumber,
    testConnection,
    save: handleSubmit(saveValues),
    cancel,
  }
}

const getStoredCashChangerSetting = (): CashChangerSettingFormValues => ({
  cashChangerSerialNumber: localStorage.getItem(cashChangerStorageKey) || '',
})
